import type { AgentSignals } from "@sill/shared/lib/signals-copy";

import { api } from "./api";

/**
 * Wire types for the M5 consumer offers surfaces
 * (`backend/internal/services/marketplace/offers/types.go` +
 * `ports.go`). Field names/casing mirror the Go JSON tags exactly —
 * this file is the FE half of the buyer-facing DTO contract
 * (task:m5-buyer-dto, task:m5-agent-detail-endpoint).
 */

export type OfferInboxKind = "active" | "standing";
export type OfferDeliveryState = "none" | "pending_labeled" | "verified";
export type OfferBuyerTier = "t1" | "t2" | "t3";

export interface OfferIncentiveItem {
  type: string;
  value?: number;
}

export interface OfferItemLanes {
  rebate?: OfferIncentiveItem[];
  cash?: OfferIncentiveItem[];
  perks?: OfferIncentiveItem[];
}

export interface OfferForBuyer {
  kind: OfferInboxKind;
  offer_id?: string;
  pro_user_id: string;
  pro_display_name?: string;
  pro_photo_url?: string;
  pro_brokerage?: string;
  delivery_state?: OfferDeliveryState;
  buyer_tier: OfferBuyerTier;
  commission_pct?: number | null;
  items: OfferItemLanes;
  note?: string;
  expires_at?: string | null;
  locked?: boolean;
  /** Server-derived relationship state for an offer from the buyer's agent. */
  own_agent?: boolean;
  /**
   * The buyer's dollar-back estimate (offers `OfferForBuyer.EstimatedSavings`).
   * Always serialized by Go; `null` means the buyer has no basis, never a
   * zero estimate. Only the inbox read fills it — agent-detail ladder entries
   * and browse standing offers send `null` (those DTOs carry their own
   * top-level estimate fields).
   */
  estimated_savings?: OfferEstimatedSavings | null;
}

export interface StandingMatch {
  pro_user_id: string;
  pro_display_name?: string;
  pro_photo_url?: string;
  pro_brokerage?: string;
  delivery_state?: OfferDeliveryState;
  primary: OfferForBuyer;
  ladder?: OfferForBuyer[];
  /** Mirrors `primary.estimated_savings` (null when the buyer has no basis). */
  estimated_savings?: OfferEstimatedSavings | null;
}

export interface OffersInbox {
  made_for_you: OfferForBuyer[];
  available_to_you: StandingMatch[];
  celebration: boolean;
  /**
   * Server fact (plan 454 M4): the buyer's target ZIP is in an active metro.
   * Always sent (never omitted); `false` also covers a buyer with no target ZIP,
   * where `area_label` is "". The not-live state keys on `=== false`, never on
   * an empty `available_to_you`.
   */
  area_launched: boolean;
  /** Gazetteer "City, ST" for the buyer's target ZIP; "" when there is none. */
  area_label: string;
}

export interface ProIdentity {
  pro_user_id: string;
  display_name?: string;
  /** The pro's Clerk-hosted headshot; absent -> initials. */
  photo_url?: string;
  brokerage?: string;
  blurb?: string;
  delivery_state?: OfferDeliveryState;
  /** Vocabulary codes (plan 444); omitted when the pro has none. */
  specialties?: string[];
  languages?: string[];
}

export type { AgentSignals };

export interface AgentDesignation {
  code: string;
  meaning: string;
}

export interface ReviewSummary {
  avg: number;
  count: number;
  closed_count: number;
}

export type ReviewLabel = "worked_together" | "closed";
export type ReviewOrigin = "marketplace" | "invited";

export interface DisplayReview {
  stars: number;
  body: string;
  label: ReviewLabel;
  origin: ReviewOrigin;
  created_at: string;
  reviewer_line: string;
}

export interface GoogleReview {
  author_name: string;
  author_photo_url?: string;
  rating: number;
  text?: string;
  relative_time?: string;
}

export interface AgentDetailView {
  identity: ProIdentity;
  buyer_tier: OfferBuyerTier;
  ladder: OfferForBuyer[];
  my_offer?: OfferForBuyer | null;
  distance_label?: string;
  match_reasons?: string[];
  service_area_cities?: string[];
  buyer_pro_state?: "" | "requested" | "your_agent";
  standing_estimated_savings?: OfferEstimatedSavings | null;
  active_estimated_savings?: OfferEstimatedSavings | null;
  /**
   * M11 (backend/internal/services/marketplace/offers/ports.go
   * `AgentDetailView`): never omitempty on the Go side — an explicit
   * zero rating/empty review list must render, never vanish.
   */
  review_summary: ReviewSummary;
  reviews: DisplayReview[];
  /**
   * omitempty on the Go side: absence means "section hidden" per the
   * GoogleReviewsReader fails-open contract (a nil reader or a failed
   * lookup never surfaces as an empty array here).
   */
  google_reviews?: GoogleReview[];
  signals?: AgentSignals | null;
  /** Detail-page only; `[]` when the pro has none. */
  designations?: AgentDesignation[];
}

export interface OfferEstimatedSavings {
  cents: number;
  basis_cents: number;
  basis: "target_price" | "buying_power";
}

export async function getOffersInbox(
  getToken: () => Promise<string | null>,
): Promise<OffersInbox> {
  const token = await getToken();
  return api.get<OffersInbox>("/api/marketplace/offers", token ?? undefined);
}

export async function dismissOffer(
  getToken: () => Promise<string | null>,
  offerId: string,
): Promise<void> {
  const token = await getToken();
  await api.post<void>(
    `/api/marketplace/offers/${encodeURIComponent(offerId)}/dismiss`,
    {},
    token ?? undefined,
  );
}

export async function dismissStanding(
  getToken: () => Promise<string | null>,
  proUserId: string,
): Promise<void> {
  const token = await getToken();
  await api.post<void>(
    `/api/marketplace/agents/${encodeURIComponent(proUserId)}/dismiss`,
    {},
    token ?? undefined,
  );
}

export async function getAgentDetail(
  getToken: () => Promise<string | null>,
  proUserId: string,
): Promise<AgentDetailView> {
  const token = await getToken();
  return api.get<AgentDetailView>(
    `/api/marketplace/agents/${encodeURIComponent(proUserId)}`,
    token ?? undefined,
  );
}
